import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase, getMatchWithDetailsByBroadcastId, getTeams, calculateDuration, getTournament, getPlayerOfMatch, PlayerOfMatchWinner } from '@/lib/supabase';
import { SetRecord, Rally, Match, MatchType, MATCH_CONFIG } from '@/types';
import { getSetScoreColor } from '@/lib/setScoreColor';
import { TeamLogo } from '@/components/TeamLogo';
import { resolveServingInfo, ServingParticipant } from '@/lib/matchUtils';

const FALLBACK_POLL_INTERVAL = 30000;

type MatchData = Match & {
  sets: (SetRecord & { rallies: Rally[] })[];
  started_at?: string | null;
  ended_at?: string | null;
  live_stream_url?: string | null;
};

interface TeamInfo {
  id: string;
  name: string;
  shortName: string;
  color: string;
  logoUrl?: string | null;
  players?: { id: string; name: string }[];
}


function formatTimer(startedAt: string | null, endedAt: string | null, _isLive: boolean): string {
  if (!startedAt) return '00:00';
  const start = new Date(startedAt).getTime();
  const end = endedAt ? new Date(endedAt).getTime() : Date.now();
  const diff = Math.max(0, Math.floor((end - start) / 1000));
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  if (h > 0) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function getSetDuration(set: { startedAt?: string | null; started_at?: string | null; endedAt?: string | null; ended_at?: string | null }): string | null {
  const started = set.startedAt ?? set.started_at ?? null;
  const ended = set.endedAt ?? set.ended_at ?? null;
  return calculateDuration(started, ended);
}

// Collapsible Set Card Component - matches scorer styling
function SetCard({
  set,
  setNumber,
  teamA,
  teamB,
  isLive = false,
  defaultExpanded = false,
}: {
  set: any;
  setNumber: number;
  teamA: TeamInfo;
  teamB: TeamInfo;
  isLive?: boolean;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const winnerId = set.winner_id ?? set.winnerId;
  const scoreA = set.score_a ?? set.scoreA ?? 0;
  const scoreB = set.score_b ?? set.scoreB ?? 0;
  const winner = winnerId === teamA.id ? teamA : winnerId === teamB.id ? teamB : null;
  void getSetScoreColor(set, teamA, teamB);
  const rallies = set.rallies || [];
  const duration = getSetDuration(set);

  // Normalize rallies for display
  const normalizedRallies = rallies.map((r: any) => ({
    id: r.id,
    rallyNumber: r.rally_number ?? r.rallyNumber,
    servingTeamId: r.serving_team_id ?? r.servingTeamId,
    serverId: r.server_id ?? r.serverId,
    pointWonById: r.point_won_by_id ?? r.pointWonById,
    lostReason: r.lost_reason ?? r.lostReason,
    pointWinnerId: r.point_winner_id ?? r.pointWinnerId,
  })).sort((a: any, b: any) => a.rallyNumber - b.rallyNumber);

  const allPlayers = [...(teamA.players || []), ...(teamB.players || [])];

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden">
      {/* Set Header - clickable to expand/collapse */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-surface-raised transition-colors"
      >
        <div className="flex items-center gap-3">
          {/* Live indicator for current set */}
          {isLive && (
            <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-error/10 text-error text-xs font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-error animate-pulse" />
              LIVE
            </span>
          )}
          <span className="text-text-muted text-xs font-medium">SET {setNumber}</span>
          {winner && (
            <span
              className="text-xs font-bold px-2 py-0.5 rounded"
              style={{ backgroundColor: `${winner.color}20`, color: winner.color }}
            >
              {winner.shortName} WON
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Score display */}
          <div className="flex items-center gap-2">
            <span className="font-mono-nums font-bold text-lg" style={{ color: teamA.color }}>
              {String(scoreA).padStart(2, '0')}
            </span>
            <span className="text-text-muted">-</span>
            <span className="font-mono-nums font-bold text-lg" style={{ color: teamB.color }}>
              {String(scoreB).padStart(2, '0')}
            </span>
          </div>
          {duration && (
            <span className="text-xs text-text-muted hidden sm:inline">{duration}</span>
          )}
          {/* Expand/collapse arrow */}
          <svg
            className={`w-4 h-4 text-text-muted transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded Rally History */}
      {expanded && (
        <div className="border-t border-border">
          {/* Column Headers - matches RallyHistoryModal */}
          <div className="bg-surface-raised px-3 py-2 flex items-center gap-2 text-[10px] font-semibold uppercase text-text-muted">
            <span className="w-7 text-center">#</span>
            <span className="w-8">SERV</span>
            <span className="flex-1">SERVER</span>
            <span className="w-3 text-center">→</span>
            <span className="w-8">WON</span>
            <span className="w-12">REASON</span>
            <span className="flex-1">WINNER</span>
          </div>

          {/* Rally List */}
          <div className="divide-y divide-border max-h-80 overflow-y-auto">
            {normalizedRallies.length === 0 ? (
              <div className="flex items-center justify-center h-16">
                <p className="text-text-muted text-sm">No rallies recorded</p>
              </div>
            ) : (
              normalizedRallies.map((rally: any) => {
                const rallyServingTeam = rally.servingTeamId === teamA.id ? teamA : teamB;
                const rallyWinningTeam = rally.pointWonById === teamA.id ? teamA : teamB;
                const server = allPlayers.find(p => p.id === rally.serverId);
                const pointWinner = allPlayers.find(p => p.id === rally.pointWinnerId);
                const isServiceBreak = rally.pointWonById !== rally.servingTeamId;

                return (
                  <div
                    key={rally.id}
                    className={`flex items-center gap-2 px-3 py-2.5 text-xs ${
                      isServiceBreak ? 'bg-error/5' : ''
                    }`}
                  >
                    {/* Rally number */}
                    <span className="w-7 text-center text-text-muted font-mono-nums">
                      {rally.rallyNumber}
                    </span>
                    {/* Serving team badge */}
                    <span
                      className="w-8 font-bold text-center"
                      style={{ color: rallyServingTeam?.color ?? '#52525B' }}
                    >
                      {rallyServingTeam?.shortName ?? '—'}
                    </span>
                    {/* Server name */}
                    <span
                      className="flex-1 truncate"
                      style={{ color: rallyServingTeam?.color ?? '#A1A1AA' }}
                    >
                      {server?.name ?? '—'}
                    </span>
                    {/* Arrow */}
                    <span className="w-3 text-center text-text-muted">→</span>
                    {/* Winning team badge */}
                    <span
                      className="w-8 font-bold text-center"
                      style={{ color: rallyWinningTeam?.color ?? '#52525B' }}
                    >
                      {rallyWinningTeam?.shortName ?? '—'}
                    </span>
                    {/* Lost reason */}
                    <span className="w-12 text-text-muted font-medium">
                      {rally.lostReason ?? '—'}
                    </span>
                    {/* Point winner */}
                    <span
                      className="flex-1 truncate"
                      style={{ color: rallyWinningTeam?.color ?? '#A1A1AA' }}
                    >
                      {pointWinner?.name ?? ''}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {/* Rally count footer */}
          <div className="px-4 py-2 border-t border-border bg-surface-raised text-xs text-text-muted text-center">
            {normalizedRallies.length} {normalizedRallies.length === 1 ? 'rally' : 'rallies'}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PublicLiveScoreboard() {
  const { broadcastId } = useParams<{ broadcastId: string }>();
  const navigate = useNavigate();
  const [match, setMatch] = useState<MatchData | null>(null);
  const [teamA, setTeamA] = useState<TeamInfo | null>(null);
  const [teamB, setTeamB] = useState<TeamInfo | null>(null);
  const [currentServerName, setCurrentServerName] = useState<string | null>(null);
  const [tournamentName, setTournamentName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [elapsedTime, setElapsedTime] = useState('00:00');
  const [playerOfMatch, setPlayerOfMatch] = useState<PlayerOfMatchWinner[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Track real match ID and current set ID for realtime subscriptions
  const matchIdRef = useRef<string | null>(null);
  const currentSetIdRef = useRef<string | null>(null);

  const fetchData = useCallback(async (silent = false) => {
    if (!broadcastId) return;
    if (!silent) setIsRefreshing(true);
    try {
      const data = await getMatchWithDetailsByBroadcastId(broadcastId);

      if (!data) {
        setError('Match not found');
        return;
      }

      const matchData = data as unknown as MatchData;
      matchData.liveStreamUrl = matchData.live_stream_url ?? matchData.liveStreamUrl ?? null;
      setMatch(matchData);

      // Capture IDs for realtime subscriptions
      matchIdRef.current = matchData.id ?? (matchData as any).match_id ?? null;
      const liveSet = (matchData.sets || []).find((s: any) => !(s.winner_id || s.winnerId));
      currentSetIdRef.current = liveSet?.id ?? null;

      const teamAId = matchData.teamAId ?? (matchData as any).team_a_id;
      const teamBId = matchData.teamBId ?? (matchData as any).team_b_id;

      const teamsData = await getTeams();
      const teamAMap = teamsData.find((t: any) => t.id === teamAId) as any;
      const teamBMap = teamsData.find((t: any) => t.id === teamBId) as any;

      if (teamAMap) {
        setTeamA({
          id: teamAMap.id,
          name: teamAMap.name,
          shortName: teamAMap.short_name ?? teamAMap.shortName,
          color: teamAMap.color,
          logoUrl: teamAMap.logo_url ?? teamAMap.logoUrl,
          players: teamAMap.players ?? [],
        });
      }
      if (teamBMap) {
        setTeamB({
          id: teamBMap.id,
          name: teamBMap.name,
          shortName: teamBMap.short_name ?? teamBMap.shortName,
          color: teamBMap.color,
          logoUrl: teamBMap.logo_url ?? teamBMap.logoUrl,
          players: teamBMap.players ?? [],
        });
      }

      const teamAParticipant: ServingParticipant | null = teamAMap ? {
        id: teamAMap.id,
        shortName: teamAMap.short_name ?? teamAMap.shortName,
        color: teamAMap.color,
        players: teamAMap.players ?? [],
      } : null;
      const teamBParticipant: ServingParticipant | null = teamBMap ? {
        id: teamBMap.id,
        shortName: teamBMap.short_name ?? teamBMap.shortName,
        color: teamBMap.color,
        players: teamBMap.players ?? [],
      } : null;

      const { serverName } = resolveServingInfo(
        (matchData as any).current_serving_team_id,
        (matchData as any).current_server_id,
        teamAParticipant,
        teamBParticipant,
      );
      setCurrentServerName(serverName);

      const tournamentId = matchData.tournamentId ?? (matchData as any).tournament_id;
      if (tournamentId) {
        try {
          const tournament = await getTournament(tournamentId);
          if (tournament) setTournamentName(tournament.name);
        } catch {
          // Tournament name is optional
        }
      }

      setError(null);
    } catch (err) {
      console.error('[PublicLiveScoreboard] Error:', err);
      setError('Failed to load match data');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [broadcastId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Realtime subscriptions: fires on match/set/rally changes and refetches.
  // A 30s fallback poll runs in parallel to recover from dropped WS connections.
  useEffect(() => {
    if (!broadcastId) return;

    const refresh = () => fetchData(true);

    // Subscribe to match-level changes (status, serving team, etc.)
    const matchChannel = supabase
      .channel(`tb-scoreboard-match-${broadcastId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'matches' }, refresh)
      .subscribe();

    // Subscribe to set-level changes (scores, set won, new set created)
    const setsChannel = supabase
      .channel(`tb-scoreboard-sets-${broadcastId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'sets' }, refresh)
      .subscribe();

    // Fallback poll — safety net if the realtime connection drops
    pollRef.current = setInterval(() => {
      if (!document.hidden) refresh();
    }, FALLBACK_POLL_INTERVAL);

    return () => {
      supabase.removeChannel(matchChannel);
      supabase.removeChannel(setsChannel);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [broadcastId, fetchData]);

  // Realtime subscription for rally changes on the current (live) set only.
  // Rebuilt only when the active set ID changes, not on every fetchData tick.
  useEffect(() => {
    const setId = currentSetIdRef.current;
    if (!setId) return;

    const refresh = () => fetchData(true);

    const channel = supabase
      .channel(`tb-scoreboard-rallies-${setId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'rallies', filter: `set_id=eq.${setId}` }, refresh)
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  // Re-subscribe whenever the live set changes (new set started)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.id, (match?.sets || []).find((s: any) => !(s.winner_id || s.winnerId))?.id]);

  useEffect(() => {
    if (!match) return;
    const isLive = match.status === 'live' || match.status === 'set-won' || match.status === 'first-server';
    const startedAt = match.started_at ?? (match as any).startedAt;
    const endedAt = match.ended_at ?? (match as any).endedAt;

    if (!startedAt) {
      setElapsedTime('00:00');
      return;
    }

    setElapsedTime(formatTimer(startedAt, endedAt, isLive));

    if (isLive) {
      timerRef.current = setInterval(() => {
        setElapsedTime(formatTimer(startedAt, endedAt, true));
      }, 1000);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [match?.status, match?.started_at, match?.ended_at]);

  // Fetch Player of Match when match is complete
  useEffect(() => {
    const matchId = match?.id;
    if (!matchId || match?.status !== 'match-won') { setPlayerOfMatch([]); return; }
    getPlayerOfMatch(matchId)
      .then(setPlayerOfMatch)
      .catch(() => setPlayerOfMatch([]));
  }, [match?.id, match?.status]);

  useEffect(() => {
    if (teamA && teamB) {
      document.title = `${teamA.shortName} vs ${teamB.shortName} - Live Score | Throwball`;
    } else {
      document.title = 'Live Score | Throwball';
    }
  }, [teamA, teamB]);

  // Loading state - dark theme
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full animate-spin mx-auto mb-4 border-4 border-border border-t-text-secondary" />
          <p className="text-sm text-text-muted">Loading match...</p>
        </div>
      </div>
    );
  }

  // Error state - dark theme
  if (error || !match) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-error/10">
            <svg className="w-8 h-8 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-text-primary mb-2">Match Not Available</h1>
          <p className="text-sm text-text-secondary mb-4">{error || 'The match you are looking for could not be found.'}</p>
          <button
            onClick={() => navigate('/live')}
            className="text-sm font-semibold text-text-secondary hover:text-text-primary transition-colors"
          >
            Back to Live Matches
          </button>
        </div>
      </div>
    );
  }

  const isLive = match.status === 'live' || match.status === 'set-won' || match.status === 'first-server';
  const isCompleted = match.status === 'match-won';

  const sets = match.sets || [];
  const completedSets = sets.filter((s: any) => (s.winnerId ?? s.winner_id));
  const currentSet = sets.find((s: any) => !(s.winnerId ?? s.winner_id)) || sets[sets.length - 1];
  const cs = currentSet as any;
  const currentSetNumber = cs ? (cs.setNumber ?? cs.set_number ?? 1) : 1;
  const matchType = ((match as any).matchType ?? (match as any).match_type ?? 'league') as MatchType;
  const matchConfig = MATCH_CONFIG[matchType as keyof typeof MATCH_CONFIG] || MATCH_CONFIG.league;

  const teamAId = (match as any).teamAId ?? (match as any).team_a_id;
  const teamBId = (match as any).teamBId ?? (match as any).team_b_id;
  const teamASetsWon = completedSets.filter((s: any) => (s.winnerId ?? s.winner_id) === teamAId).length;
  const teamBSetsWon = completedSets.filter((s: any) => (s.winnerId ?? s.winner_id) === teamBId).length;

  const currentSetScoreA = cs ? (cs.scoreA ?? cs.score_a ?? 0) : 0;
  const currentSetScoreB = cs ? (cs.scoreB ?? cs.score_b ?? 0) : 0;

  // Use shared resolveServingInfo helper for consistent serving state
  const teamAParticipant: ServingParticipant | null = teamA ? {
    id: teamA.id,
    shortName: teamA.shortName,
    color: teamA.color,
    logoUrl: teamA.logoUrl,
    players: teamA.players ?? [],
  } : null;
  const teamBParticipant: ServingParticipant | null = teamB ? {
    id: teamB.id,
    shortName: teamB.shortName,
    color: teamB.color,
    logoUrl: teamB.logoUrl,
    players: teamB.players ?? [],
  } : null;

  const { servingTeam: resolvedServingTeam } = resolveServingInfo(
    (match as any).current_serving_team_id,
    (match as any).current_server_id,
    teamAParticipant,
    teamBParticipant,
  );
  const servingTeam = resolvedServingTeam;

  const isTeamAServing = servingTeam?.id === teamAId;
  const isTeamBServing = servingTeam?.id === teamBId;

  const matchWinner = isCompleted && match.winnerId
    ? (match.winnerId === teamAId ? teamA : teamB)
    : null;

  const matchDuration = calculateDuration(
    match.started_at ?? (match as any).startedAt,
    match.ended_at ?? (match as any).endedAt
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header - matches scorer AppHeader style */}
      <header className="bg-surface border-b border-border px-4 py-3 safe-top sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/live')}
            className="p-2 -ml-2 rounded-lg text-text-muted hover:text-text-primary transition-colors"
            aria-label="Back"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M12 4l-6 6 6 6" />
            </svg>
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-text-primary truncate">
              {tournamentName || `Match #${(match as any).match_number ?? match.matchNumber}`}
            </h1>
            <p className="text-xs text-text-muted truncate">
              {(() => { const t = (match as any).match_type ?? match.matchType ?? ''; return t.charAt(0).toUpperCase() + t.slice(1); })()} Match
              {currentServerName && isLive && ` · ${currentServerName} serving`}
            </p>
          </div>
          <button
            onClick={() => fetchData()}
            disabled={isRefreshing}
            className="p-2 rounded-lg text-text-muted hover:text-text-primary transition-colors disabled:opacity-40"
            title="Refresh"
          >
            <svg className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto">
        {/* Match Meta Strip */}
        <div className="bg-surface border-b border-border px-4 py-2">
          <div className="flex items-center justify-between text-xs text-text-muted">
            <span>MATCH #{(match as any).match_number ?? match.matchNumber}</span>
            <div className="flex items-center gap-3">
              {!isCompleted && match.liveStreamUrl && (
                <a
                  href={match.liveStreamUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                  style={{ backgroundColor: '#DC2626', color: '#FFFFFF' }}
                >
                  <span>🎥</span>
                  <span>Watch Live</span>
                </a>
              )}
              <span className="uppercase">{(match as any).match_type ?? match.matchType}</span>
            </div>
          </div>
        </div>

        {/* ── COMPLETED MATCH VIEW ── */}
        {isCompleted ? (
          <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
            {/* MATCH COMPLETED badge */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-success/15 text-success rounded-full">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-bold">MATCH COMPLETED</span>
              </div>
            </div>

            {/* Watch Replay — centered */}
            {match.liveStreamUrl && (
              <div className="flex justify-center">
                <a
                  href={match.liveStreamUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                  style={{ backgroundColor: '#DC2626', color: '#FFFFFF' }}
                >
                  <span>🎬</span>
                  <span>Watch Replay</span>
                </a>
              </div>
            )}

            {/* Match Details */}
            {teamA && teamB && (
              <div className="rounded-xl p-4 bg-surface border border-border text-center">
                <p className="text-xs text-text-muted font-medium">
                  Match #{(match as any).match_number ?? match.matchNumber}
                  {(match as any).date ? ` · ${new Date((match as any).date + 'T00:00:00').toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                </p>
                <p className="text-sm font-bold text-text-primary mt-1">
                  <span style={{ color: teamA.color }}>{teamA.name}</span>
                  <span className="text-text-muted"> vs </span>
                  <span style={{ color: teamB.color }}>{teamB.name}</span>
                </p>
              </div>
            )}

            {/* Match Winner Card */}
            {matchWinner && (
              <div
                className="rounded-xl p-6 text-center border-2"
                style={{ backgroundColor: `${matchWinner.color}15`, borderColor: matchWinner.color }}
              >
                <p className="text-xs text-text-muted uppercase mb-3">MATCH WON</p>
                <div className="flex justify-center mb-3">
                  <TeamLogo team={matchWinner as any} size="2xl" />
                </div>
                <p className="text-2xl font-bold mb-1" style={{ color: matchWinner.color }}>
                  {matchWinner.name.toUpperCase()}
                </p>
                <p className="text-sm text-text-secondary">{matchWinner.shortName}</p>
              </div>
            )}

            {/* Player of the Match */}
            {playerOfMatch.length > 0 && (
              <div className="card border-2 border-warning/30 bg-warning/5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">🏆</span>
                  <h2 className="text-xs font-bold text-warning uppercase tracking-wider">Player Of The Match</h2>
                </div>
                <div className="space-y-3">
                  {playerOfMatch.map(p => {
                    const pomTeam = p.teamId === teamA?.id ? teamA : teamB;
                    return (
                      <div key={p.playerId} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <TeamLogo team={(pomTeam ?? { id: '', name: p.teamName, shortName: p.teamName.substring(0, 2), color: '#F59E0B', players: [] }) as any} size="sm" />
                          <div>
                            <p className="text-sm font-bold text-text-primary">{p.playerName}</p>
                            <p className="text-xs text-text-muted">{p.teamName}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-0.5 justify-end text-warning">
                            {'⭐'.repeat(Math.min(p.totalStars, 1))}
                            {p.totalStars > 3 && <span className="text-xs font-bold ml-1">×{p.totalStars}</span>}
                          </div>
                          <p className="text-[10px] text-text-muted mt-0.5">
                            {[
                              p.throwerStars > 0 && `${p.throwerStars}T`,
                              p.acerStars > 0 && `${p.acerStars}A`,
                              p.catcherStars > 0 && `${p.catcherStars}C`,
                            ].filter(Boolean).join(' ')}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Set Scores */}
            <div className="card">
              <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">SET SCORES</h2>
              <div className="space-y-2">
                {sets.map((set: any, idx: number) => {
                  const sNum = set.set_number ?? set.setNumber ?? (idx + 1);
                  const sWinnerId = set.winner_id ?? set.winnerId;
                  const sWinner = sWinnerId === teamAId ? teamA : sWinnerId === teamBId ? teamB : null;
                  return (
                    <div key={set.id ?? idx} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                      <span className="text-sm text-text-secondary w-12">Set {sNum}</span>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold w-8 text-right" style={{ color: teamA?.color }}>{teamA?.shortName}</span>
                          <span className={`font-mono text-xl font-bold ${sWinner?.id === teamAId ? 'text-text-primary' : 'text-text-muted'}`}>{set.score_a ?? set.scoreA ?? 0}</span>
                        </div>
                        <span className="text-text-muted text-sm">—</span>
                        <div className="flex items-center gap-2">
                          <span className={`font-mono text-xl font-bold ${sWinner?.id === teamBId ? 'text-text-primary' : 'text-text-muted'}`}>{set.score_b ?? set.scoreB ?? 0}</span>
                          <span className="text-xs font-semibold w-8" style={{ color: teamB?.color }}>{teamB?.shortName}</span>
                        </div>
                      </div>
                      {sWinner && (
                        <span className="text-[10px] font-bold w-16 text-right" style={{ color: sWinner.color }}>{sWinner.shortName} WON</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Match Stats */}
            <div className="card">
              <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">MATCH STATS</h2>
              <div className="space-y-1">
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-sm text-text-secondary">Sets Played</span>
                  <span className="font-mono font-bold text-text-primary">{completedSets.length}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-sm text-text-secondary">Sets Won by {teamA?.shortName}</span>
                  <span className="font-mono font-bold" style={{ color: teamA?.color }}>{teamASetsWon}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-sm text-text-secondary">Sets Won by {teamB?.shortName}</span>
                  <span className="font-mono font-bold" style={{ color: teamB?.color }}>{teamBSetsWon}</span>
                </div>
                {matchDuration && (
                  <div className="flex items-center justify-between py-2">
                    <span className="flex items-center gap-2 text-sm text-text-secondary">
                      <span className="w-2 h-2 rounded-full bg-success inline-block" />
                      Match Duration
                    </span>
                    <span className="font-mono font-bold text-text-primary">{matchDuration}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Completed Sets accordion */}
            <div className="space-y-2">
              <h2 className="text-xs font-bold uppercase text-text-muted tracking-wider">Completed Sets</h2>
              {completedSets.map((set: any, idx: number) => (
                <SetCard key={set.id ?? idx} set={set} setNumber={set.set_number ?? set.setNumber ?? (idx + 1)}
                  teamA={teamA!} teamB={teamB!} isLive={false} defaultExpanded={false} />
              ))}
            </div>

            <div className="text-center py-4 border-t border-border">
              <p className="text-xs text-text-muted">Throwball Live Scoreboard</p>
            </div>
          </div>

        ) : (
          /* ── LIVE MATCH VIEW ── */
          <div>
          <div className="bg-surface border-b border-border">
          <div className="grid grid-cols-2 divide-x divide-border">
            {/* Team A Score */}
            <div className="py-4 px-2 sm:px-4 relative">
              {isTeamAServing && isLive && (
                <span className="absolute top-1.5 left-2 sm:left-4 text-xs font-bold text-serving">
                  SERVING
                </span>
              )}
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <TeamLogo team={teamA! as any} size="sm" />
                <span className="text-xs font-semibold uppercase" style={{ color: teamA?.color }}>{teamA?.shortName}</span>
              </div>
              <p className="font-mono-nums text-6xl sm:text-7xl font-bold text-center text-text-primary">
                {String(currentSetScoreA).padStart(2, '0')}
              </p>
              <div className="flex justify-center gap-1 mt-3">
                {Array.from({ length: matchConfig.maxSets }).map((_, i) => (
                  <span
                    key={i}
                    className={`w-3 h-3 rounded-full ${
                      i < teamASetsWon ? 'bg-team-a' : 'bg-border'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Team B Score */}
            <div className="py-4 px-2 sm:px-4 relative">
              {isTeamBServing && isLive && (
                <span className="absolute top-1.5 right-2 sm:right-4 text-xs font-bold text-serving">
                  SERVING
                </span>
              )}
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <span className="text-xs font-semibold uppercase" style={{ color: teamB?.color }}>{teamB?.shortName}</span>
                <TeamLogo team={teamB! as any} size="sm" />
              </div>
              <p className="font-mono-nums text-6xl sm:text-7xl font-bold text-center text-text-primary">
                {String(currentSetScoreB).padStart(2, '0')}
              </p>
              <div className="flex justify-center gap-1 mt-3">
                {Array.from({ length: matchConfig.maxSets }).map((_, i) => (
                  <span
                    key={i}
                    className={`w-3 h-3 rounded-full ${
                      i < teamBSetsWon ? 'bg-team-b' : 'bg-border'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
          </div> {/* bg-surface border-b */}

        {/* Current Set Info */}
        <div className="bg-surface-raised border-b border-border px-4 py-3">
          <div className="text-center">
            <span className="text-sm font-bold text-text-primary">
              SET {currentSetNumber} OF {matchConfig.maxSets}
            </span>
          </div>
        </div>

        {/* Serving Panel - matches scorer styling */}
        {servingTeam && currentServerName && isLive && !isCompleted && (
          <div
            className={`mx-4 mt-3 rounded-lg p-4 border transition-all ${
              servingTeam.id === teamAId ? 'glow-team-a border-team-a' : 'glow-team-b border-team-b'
            }`}
            style={{ backgroundColor: `${servingTeam.color}10` }}
          >
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-3 h-3 rounded-full bg-serving animate-pulse" />
                  <span className="text-xs font-semibold uppercase text-serving">Serving Now</span>
                </div>
                <p className="text-lg font-bold truncate" style={{ color: servingTeam.color }}>
                  {(servingTeam.shortName ?? 'TEAM').toUpperCase()}
                </p>
                <p className="text-xl font-bold truncate" style={{ color: servingTeam.color }}>
                  {currentServerName}
                </p>
              </div>
              <TeamLogo team={servingTeam as any} size="xl" className="w-16 h-16" />
            </div>
          </div>
        )}

        {/* Stats Row */}
        <div className="bg-surface border-b border-border px-4 py-3">
          <div className="flex items-center justify-between gap-4 text-xs">
            <div className="text-center">
              <span className="text-text-muted block">Sets Won</span>
              <span className="font-bold" style={{ color: teamA?.color }}>{teamASetsWon}</span>
            </div>
            <div className="text-center">
              <span className="text-text-muted block">Duration</span>
              <span className="font-bold text-text-primary">{elapsedTime}</span>
            </div>
            <div className="text-center">
              <span className="text-text-muted block">Sets Won</span>
              <span className="font-bold" style={{ color: teamB?.color }}>{teamBSetsWon}</span>
            </div>
          </div>
        </div>

        {/* Sets Section - All Collapsible */}
        <div className="px-4 py-4 space-y-3">
          <h2 className="text-xs font-bold uppercase text-text-muted tracking-wider px-1">
            SET HISTORY
          </h2>

          {/* Completed Sets - collapsed by default */}
          {completedSets.map((set: any, idx: number) => {
            const setNum = set.set_number ?? set.setNumber;
            return (
              <SetCard
                key={set.id ?? idx}
                set={set}
                setNumber={setNum}
                teamA={teamA!}
                teamB={teamB!}
                isLive={false}
                defaultExpanded={false}
              />
            );
          })}

          {/* Current/Live Set - also collapsed by default */}
          {currentSet && !isCompleted && (
            <SetCard
              set={currentSet}
              setNumber={currentSetNumber}
              teamA={teamA!}
              teamB={teamB!}
              isLive={isLive}
              defaultExpanded={false}
            />
          )}
        </div>

        {/* Footer */}
        <div className="text-center py-4 border-t border-border mt-4">
          <p className="text-xs text-text-muted">Throwball Live Scoreboard</p>
          {isLive && (
            <p className="text-[10px] text-text-muted mt-0.5">Updates in real-time</p>
          )}
        </div>
        </div>
        )}
      </main>
    </div>
  );
}
